import axios from 'axios';
import CryptoJS from 'crypto-js';
import { Data_Encryption } from './http';

function encryptPayload(payload) {
  const key_id = window._lastKeyId || Object.keys(window._SecureKeyMap).slice(-1)[0];
  const aesKey = window._SecureKeyMap[key_id];
  if (!aesKey) throw new Error("AES key not found");

  const iv = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(
    JSON.stringify(payload),
    aesKey,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  return {
    key_id,
    secure: CryptoJS.enc.Base64.stringify(encrypted.ciphertext),
    iv: CryptoJS.enc.Base64.stringify(iv),
  };
}

// Fallback HMAC secret, used only when this browser has no access token yet —
// login, OTP, public payment pages. It is compiled into this bundle and is
// therefore readable by anyone, so it proves nothing about who is calling; those
// endpoints are protected by their own means (Razorpay signature, OTP attempt
// limits, JWT on everything else).
//
// Must stay in step with Django's _LEGACY_CLIENT_SIGNING_KEY (settings.py). To
// change it, add the new value to API_SIGNING_KEY server-side FIRST, ship this,
// then retire the old one — the server accepts a list precisely so the two can
// overlap.
const SHARED_SECRET = '!nm#lviz03+k%^7r*xi@mf^+7p0l3yx2d)bk$1ec&x&*itn5cf';
// Global memory for AES keys per request
window._SecureKeyMap = window._SecureKeyMap || {};

// The signing key for THIS request.
//
// When the caller is authenticated we sign with SHA256(access token) instead of
// the bundle constant. The server derives the same value from the Authorization
// header it already receives, so nothing extra is sent and nothing secret ships
// in this file. It rotates with every token refresh, and someone who has not
// logged in cannot produce it at all.
//
// Read from the header rather than localStorage on purpose: the header is exactly
// what the server will hash, so the two can never drift apart.
function signingKeyFor(config) {
  const auth =
    config?.headers?.Authorization || config?.headers?.authorization || '';
  if (typeof auth === 'string' && auth.startsWith('Bearer ')) {
    const token = auth.slice(7).trim();
    if (token) {
      // hex digest, matching Python's hashlib.sha256(token).hexdigest()
      return CryptoJS.SHA256(token).toString(CryptoJS.enc.Hex);
    }
  }
  return SHARED_SECRET;
}

function newKeyId() {
  const key_id = `client-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  window._SecureKeyMap[key_id] = CryptoJS.SHA256(key_id); // AES key, 32 bytes
  window._lastKeyId = key_id;
  return key_id;
}

function hmacHex(message, secret) {
  return CryptoJS.HmacSHA256(
    message,
    CryptoJS.enc.Utf8.parse(secret) // match Python hmac encoding
  ).toString(CryptoJS.enc.Hex);
}

//  REQUEST INTERCEPTOR
axios.interceptors.request.use(
  (config) => {
    if (!config.headers) config.headers = {};

    // key_id first: encryptPayload reads window._lastKeyId to find the AES key.
    const key_id = newKeyId();

    // Encrypt before signing, so the signature covers what is actually sent.
    if (Data_Encryption && config.data && !(config.data instanceof FormData)) {
      config.data = { encrypted: encryptPayload(config.data) };
    }

    const secret = signingKeyFor(config);

    config.headers['X-Client-Key'] = key_id;
    config.headers['X-Key-Id'] = key_id; // Needed for encryption middleware
    // Unbound signature — what every server version understands.
    config.headers['X-Key-Signature'] = hmacHex(key_id, secret);

    // Body-bound signature, in its own header. Any edit to the payload in flight
    // changes the digest and invalidates it. It lives beside the unbound one
    // rather than replacing it so that a serialisation difference can never break
    // a request: the server only insists on this form once
    // API_REQUIRE_BODY_BINDING is switched on.
    //
    // Skipped for FormData: the multipart boundary is chosen by the browser, so
    // the bytes cannot be predicted here (the server skips decryption for those
    // requests too).
    if (!(config.data instanceof FormData)) {
      try {
        const bodyText = config.data === undefined || config.data === null
          ? ''
          : (typeof config.data === 'string' ? config.data : JSON.stringify(config.data));
        const digest = CryptoJS.SHA256(bodyText).toString(CryptoJS.enc.Hex);
        config.headers['X-Body-Digest'] = digest;
        config.headers['X-Key-Signature-V2'] = hmacHex(`${key_id}|${digest}`, secret);
      } catch (e) {
        // A body that will not serialise here is one we simply do not bind.
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// --- Worker-Based Decryption Logic ---
const decryptionWorker = new Worker(new URL('./utils/decryption-worker.js', import.meta.url), { type: 'module' });
const pendingDecryptions = new Map();

decryptionWorker.onmessage = (e) => {
  const { success, data, error, key_id } = e.data;
  const promise = pendingDecryptions.get(key_id);
  if (promise) {
    if (success) promise.resolve(data);
    else promise.reject(new Error(error));
    pendingDecryptions.delete(key_id);
  }
};

function decryptWithWorker(key_id, encrypted, iv_b64, aesKey) {
  return new Promise((resolve, reject) => {
    pendingDecryptions.set(key_id, { resolve, reject });
    decryptionWorker.postMessage({ 
      key_id, encrypted, iv_b64, 
      aesKeyWordArray: { words: aesKey.words, sigBytes: aesKey.sigBytes } 
    });
  });
}

// Decrypt a {key_id, secure, iv} envelope. Reports which of the three cases
// happened so callers can keep their own fallback behaviour:
//   "plain"  - body was never encrypted, nothing to do
//   "nokey"  - encrypted, but this client has no AES key for it
//   "failed" - encrypted, key present, decryption threw
//   "ok"     - decrypted, payload in .data
async function decryptEnvelope(body) {
  const key_id = body?.key_id;
  const encrypted = body?.secure;
  const iv_b64 = body?.iv;

  // Only decrypt if all three are present
  if (!(key_id && encrypted && iv_b64)) return { status: "plain" };

  const aesKey = window._SecureKeyMap?.[key_id];
  if (!aesKey) {
    // console.warn(" AES key not found for", key_id);
    return { status: "nokey" };
  }

  try {
    const data = await decryptWithWorker(key_id, encrypted, iv_b64, aesKey);
    delete window._SecureKeyMap[key_id];
    return { status: "ok", data };
  } catch (err) {
    return { status: "failed" };
  }
}

//  Global Axios Response Interceptor
axios.interceptors.response.use(
  async (response) => {
    const result = await decryptEnvelope(response.data);

    if (result.status === "ok") {
      response.data = result.data;
    } else if (result.status === "failed") {
      if (response.data) response.data.data = [];
    }

    return response;
  },
  async (error) => {
    // Error bodies are encrypted exactly like successful ones. Without this,
    // every 4xx/5xx surfaces to the UI as a raw {"secure":...,"iv":...} blob
    // and the real message ("no master content found", etc.) is lost.
    const result = await decryptEnvelope(error?.response?.data);

    if (result.status === "ok") {
      error.response.data = result.data;
    }

    // Concurrent-login policy: this session was superseded by a sign-in on
    // another device of the same class. Nothing here is recoverable by retrying,
    // so drop the credentials and send the user back to login with a reason.
    const body = error?.response?.data;
    const superseded =
      error?.response?.status === 401 &&
      (body?.code === "SESSION_SUPERSEDED" ||
        String(body?.detail || body?.error || "").includes("SESSION_SUPERSEDED"));

    // Tenant scope: this request named a different institute than the one signed
    // into the token. TenantScopeMiddleware only logs while MULTI_TENANT_ENFORCE
    // is off; once it is on, the request is refused with this code.
    //
    // Never the user's mistake — it means a call site is still sourcing its
    // institute from localStorage or a hardcoded 0 rather than from the claim
    // (see services/tenantClaims.ts `resolveInstituteId`). Logged with the
    // offending URL, which is the one thing needed to find and fix it.
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === "TENANT_MISMATCH"
    ) {
      console.error(
        "TENANT_MISMATCH — request institute disagrees with the signed session.",
        error?.config?.method,
        error?.config?.url,
        error?.config?.params,
      );
    }

    if (superseded && !window.__sessionSupersededHandled) {
      window.__sessionSupersededHandled = true;
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        /* storage may be unavailable; the redirect below still applies */
      }
      window.location.href = "/?signedOut=other-device";
    }

    return Promise.reject(error);
  }
);